import { IcoTrash16 } from '@onefootprint/icons';
import type {
  CustomRequirementCondition,
  CustomRequirementConditionExpression,
  DataIdentifier,
} from '@onefootprint/request-types/dashboard';
import { Button, IconButton, Select, TextInput, Toggle } from '@onefootprint/ui';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { CustomQuestionsFormData, Question } from '../types';

type SettingsLogicProps = {
  questionIndex: number;
  availableQuestions: Exclude<Question, { kind: 'text_block' }>[];
  canEdit: boolean;
};

export type SettingsLogicRef = {
  hasIncompleteRules: () => boolean;
  clearLogic: () => void;
  enableLogic: () => void;
};

type LogicRule = {
  field: string;
  op: 'eq' | 'not_eq' | 'exists';
  value?: string | number | boolean;
};

const SettingsLogic = forwardRef<SettingsLogicRef, SettingsLogicProps>(
  ({ questionIndex, availableQuestions, canEdit }, ref) => {
    const { t } = useTranslation('playbook-custom-questions');
    const { control, setValue } = useFormContext<CustomQuestionsFormData>();
    const currentQuestion = useWatch({ control, name: `questions.${questionIndex}` });

    const [enabled, setEnabled] = useState(!!currentQuestion?.condition);
    const [operator, setOperator] = useState<'and' | 'or'>('and');
    const [rules, setRules] = useState<LogicRule[]>([]);

    // Initialize local state from form data
    useEffect(() => {
      if (currentQuestion?.condition) {
        const condition = currentQuestion.condition;
        const newRules: LogicRule[] = [];
        let detectedOperator: 'and' | 'or' = 'and';

        const processCondition = (cond: CustomRequirementCondition) => {
          if (cond.kind === 'expression') {
            if ('value' in cond && (cond.op === 'eq' || cond.op === 'not_eq')) {
              newRules.push({
                field: cond.field,
                op: cond.op,
                value: cond.value as string | number | boolean,
              });
            } else if (cond.op === 'exists') {
              newRules.push({
                field: cond.field,
                op: cond.op,
              });
            }
          } else if (cond.kind === 'and') {
            detectedOperator = 'and';
            cond.conditions.forEach(processCondition);
          } else if (cond.kind === 'or') {
            detectedOperator = 'or';
            cond.conditions.forEach(processCondition);
          }
        };

        processCondition(condition);
        setRules(newRules);
        setOperator(detectedOperator);
        setEnabled(true);
      } else {
        setRules([]);
        setOperator('and');
        setEnabled(false);
      }
    }, [currentQuestion?.condition]);

    const updateCondition = (newRules: LogicRule[], isEnabled: boolean, currentOperator: 'and' | 'or' = operator) => {
      if (!isEnabled) {
        setValue(`questions.${questionIndex}.condition`, undefined, { shouldDirty: true });
        return;
      }

      if (newRules.length === 0) {
        setValue(`questions.${questionIndex}.condition`, undefined, { shouldDirty: true });
        return;
      }

      const expressions: CustomRequirementConditionExpression[] = newRules.map(rule => {
        switch (rule.op) {
          case 'exists':
            return {
              kind: 'expression',
              field: rule.field as DataIdentifier,
              op: 'exists',
            };
          case 'eq':
            return {
              kind: 'expression',
              field: rule.field as DataIdentifier,
              op: 'eq',
              value: rule.value ?? '',
            };
          case 'not_eq':
            return {
              kind: 'expression',
              field: rule.field as DataIdentifier,
              op: 'not_eq',
              value: rule.value ?? '',
            };
          default: {
            const _exhaustiveCheck: never = rule.op;
            throw new Error(`Unsupported logic operator: ${_exhaustiveCheck}`);
          }
        }
      });

      let condition: CustomRequirementCondition;

      if (expressions.length === 1) {
        condition = expressions[0];
      } else {
        condition = {
          kind: currentOperator,
          conditions: expressions,
        };
      }

      setValue(`questions.${questionIndex}.condition`, condition, { shouldDirty: true });
    };

    const handleToggle = (checked: boolean) => {
      setEnabled(checked);
      if (checked && rules.length === 0) {
        const defaultQuestion = availableQuestions[0];
        if (defaultQuestion) {
          const newRules: LogicRule[] = [{ field: defaultQuestion.dataIdentifier, op: 'eq', value: '' }];
          setRules(newRules);
          updateCondition(newRules, true);
        }
      } else {
        updateCondition(rules, checked);
      }
    };

    const handleAddRule = () => {
      const defaultQuestion = availableQuestions[0];
      if (!defaultQuestion) return;

      const newRules: LogicRule[] = [...rules, { field: defaultQuestion.dataIdentifier, op: 'eq', value: '' }];
      setRules(newRules);
      updateCondition(newRules, true);
    };

    const handleRemoveRule = (index: number) => {
      const newRules = rules.filter((_, i) => i !== index);
      setRules(newRules);
      updateCondition(newRules, true);

      if (newRules.length === 0) {
        setEnabled(false);
        updateCondition([], false);
      }
    };

    const handleOperatorChange = (newOperator: 'and' | 'or') => {
      setOperator(newOperator);
      updateCondition(rules, true, newOperator);
    };

    const handleUpdateRule = (index: number, updates: Partial<LogicRule>) => {
      const newRules = rules.map((rule, i) => (i === index ? { ...rule, ...updates } : rule));

      // Reset value if field changes, because type might be different
      if (updates.field) {
        const targetQuestion = availableQuestions.find(q => q.dataIdentifier === updates.field);
        if (targetQuestion) {
          if (newRules[index].op !== 'exists') {
            newRules[index].value = '';
          } else {
            delete newRules[index].value;
          }
        }
      }

      if (updates.op === 'exists') {
        delete newRules[index].value;
      } else if ((updates.op === 'eq' || updates.op === 'not_eq') && newRules[index].value === undefined) {
        newRules[index].value = '';
      }

      setRules(newRules);
      updateCondition(newRules, true);
    };

    const hasIncompleteRules = () => {
      return (
        enabled &&
        rules.some(rule => {
          if (typeof rule.value === 'string' && rule.value.trim() === '') return true;
          if (rule.op === 'exists') return false;
          if (rule.value === undefined || rule.value === null) return true;
          return false;
        })
      );
    };

    const clearLogic = () => {
      setEnabled(false);
      setRules([]);
      updateCondition([], false);
    };

    const enableLogic = () => {
      if (!enabled) {
        handleToggle(true);
      }
    };

    useImperativeHandle(ref, () => ({
      hasIncompleteRules,
      clearLogic,
      enableLogic,
    }));

    const renderValueInput = (rule: LogicRule, index: number) => {
      if (rule.op === 'exists') return null;

      const targetQuestion = availableQuestions.find(q => q.dataIdentifier === rule.field);
      if (!targetQuestion) return null;

      if (
        targetQuestion.kind === 'multiple_choice' ||
        targetQuestion.kind === 'dropdown' ||
        targetQuestion.kind === 'checkboxes'
      ) {
        const options = targetQuestion.options.map(opt => ({ label: opt.label, value: opt.label }));
        return (
          <Select
            value={rule.value as string}
            onChange={val => handleUpdateRule(index, { value: val })}
            options={options}
            size="compact"
          />
        );
      }

      if (targetQuestion.kind === 'checkbox') {
        return (
          <Select
            value={String(rule.value)}
            onChange={val => handleUpdateRule(index, { value: val === 'true' })}
            options={[
              { label: t($ => $.logic.checked), value: 'true' },
              { label: t($ => $.logic.unchecked), value: 'false' },
            ]}
            size="compact"
          />
        );
      }

      if (targetQuestion.kind === 'number') {
        return (
          <TextInput
            type="number"
            value={String(rule.value)}
            onChange={e => handleUpdateRule(index, { value: Number(e.target.value) || 0 })}
            size="compact"
          />
        );
      }

      return (
        <TextInput
          value={rule.value as string}
          onChange={e => handleUpdateRule(index, { value: e.target.value })}
          size="compact"
        />
      );
    };

    return (
      <div className="flex flex-col gap-4">
        <Toggle
          checked={enabled}
          onChange={handleToggle}
          disabled={!canEdit || availableQuestions.length === 0}
          label={t($ => $.logic.enableConditionalLogic)}
          size="compact"
          labelPlacement="left"
          fullWidth
        />

        {enabled && (
          <fieldset disabled={!canEdit} className="contents">
            <hr className="border-t border-tertiary" />
            <div className="flex flex-col gap-4">
              <p className="text-sm font-medium text-primary">{t($ => $.logic.showWhen)}</p>
              <div className="flex flex-col gap-4">
                {rules.map((rule, ruleIndex) => (
                  // Editable, add/remove-in-place list with stateful Selects per row; positional identity
                  // is correct here — a content-derived key collides on blank rules and remounts rows mid-edit.
                  // biome-ignore lint/suspicious/noArrayIndexKey: see above
                  <div key={`${rule.field}-${ruleIndex}`} className="flex flex-col gap-3">
                    {ruleIndex > 0 && (
                      <div className="w-[81px]">
                        <Select
                          value={operator}
                          onChange={val => handleOperatorChange(val as 'and' | 'or')}
                          options={[
                            { label: t($ => $.logic.and), value: 'and' },
                            { label: t($ => $.logic.or), value: 'or' },
                          ]}
                          size="compact"
                          disabled={!canEdit}
                        />
                      </div>
                    )}
                    <div className="flex flex-col gap-1 relative group">
                      {/* Row 1: Question Selector with icon button */}
                      <div className="flex gap-1 items-center">
                        <div className="flex-1 min-w-0">
                          <Select
                            value={rule.field}
                            onChange={val => handleUpdateRule(ruleIndex, { field: val })}
                            options={availableQuestions.map(q => ({
                              label: q.label || t($ => $.logic.untitled),
                              value: q.dataIdentifier,
                            }))}
                            size="compact"
                            placeholder={t($ => $.logic.selectQuestion)}
                            disabled={!canEdit}
                          />
                        </div>
                        {canEdit && (
                          <IconButton
                            icon={IcoTrash16}
                            size="tiny"
                            variant="ghost"
                            destructive
                            onClick={() => handleRemoveRule(ruleIndex)}
                            aria-label={t($ => $.logic.removeCondition)}
                          />
                        )}
                      </div>

                      {/* Row 2: Operator */}
                      <Select
                        value={rule.op}
                        onChange={val => handleUpdateRule(ruleIndex, { op: val as 'eq' | 'not_eq' | 'exists' })}
                        options={[
                          { label: t($ => $.logic.equals), value: 'eq' },
                          { label: t($ => $.logic.notEquals), value: 'not_eq' },
                          { label: t($ => $.logic.exists), value: 'exists' },
                        ]}
                        size="compact"
                        disabled={!canEdit}
                      />

                      {/* Row 3: Value */}
                      {renderValueInput(rule, ruleIndex)}
                    </div>
                  </div>
                ))}

                {canEdit && (
                  <Button variant="secondary" size="compact" onClick={handleAddRule}>
                    {t($ => $.logic.addCondition)}
                  </Button>
                )}
              </div>
            </div>
          </fieldset>
        )}
      </div>
    );
  },
);

SettingsLogic.displayName = 'SettingsLogic';

export default SettingsLogic;
